// client/src/flow/components/FormScreen.js
import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Phone,
  Mail,
  FileText,
  AlertCircle,
  Calendar,
  MapPin,
  User,
  Users,
  Send,
  CheckCircle,
  Download,
  Home,
  Copy,
  Printer,
  Check,
  X,
  Plus,
  Edit3,
  Trash2,
  Settings,
  Info,
  Hash,
  Type,
  AlignLeft,
  List,
  Radio,
  CheckSquare,
  Image,
  Zap,
  Package,
  Layout,
  Heart,
  Star,
  Sun,
  Moon,
  Book,
  Shield,
  Award,
  Gift,
  MessageCircle,
  Bell,
  Save,
  Search,
  RefreshCw,
  HelpCircle,
  Folder,
  Database,
  ArrowRight,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Clock,
  Smile,
  Globe,
  Building,
  Lock,
  Key,
  Flag,
  Tag,
  Bookmark,
  Briefcase,
  Circle,
  Square,
  Triangle,
  Activity,
  Target,
  Layers,
  ExternalLink,
  Navigation,
  Link2,
  Loader,
  Smartphone,
  UserCheck,
  Mic,
  Rocket,
  GraduationCap
} from "lucide-react";
import "./FormScreen.css";
import AISearchBox from './AISearchBox';
import UgitsLogo from './UgitsLogo';

import { convertWhatsAppUrl } from '../../utils/whatsappUtils';

// Mapeamento de ícones
const ICON_MAP = {
  // Ações
  Check: Check,
  X: X,
  Plus: Plus,
  Edit: Edit3,
  Edit3: Edit3,
  Trash: Trash2,
  Trash2: Trash2,
  Save: Save,
  Copy: Copy,
  Download: Download,
  Send: Send,
  Search: Search,
  RefreshCw: RefreshCw,
  ExternalLink: ExternalLink,
  Link2: Link2,
  Navigation: Navigation,
  Loader: Loader,

  // Religiosos/Espirituais
  Heart: Heart,
  Star: Star,
  Sun: Sun,
  Moon: Moon,
  Book: Book,
  Shield: Shield,
  Award: Award,
  Gift: Gift,

  // Comunicação
  Mail: Mail,
  Phone: Phone,
  MessageCircle: MessageCircle,
  Bell: Bell,
  Mic: Mic,

  // Interface
  Settings: Settings,
  Info: Info,
  AlertCircle: AlertCircle,
  HelpCircle: HelpCircle,
  FileText: FileText,
  Folder: Folder,
  Database: Database,

  // Navegação
  ChevronRight: ChevronRight,
  ChevronLeft: ChevronLeft,
  ChevronUp: ChevronUp,
  ChevronDown: ChevronDown,
  ArrowRight: ArrowRight,
  ArrowLeft: ArrowLeft,
  ArrowUp: ArrowUp,
  ArrowDown: ArrowDown,

  // Tempo
  Calendar: Calendar,
  Clock: Clock,

  // Pessoas
  User: User,
  Users: Users,
  Smile: Smile,
  UserCheck: UserCheck,

  // Lugares
  MapPin: MapPin,
  Globe: Globe,
  Building: Building,
  Home: Home,
  Church: Home,

  // Objetos
  Lock: Lock,
  Key: Key,
  Flag: Flag,
  Tag: Tag,
  Bookmark: Bookmark,
  Briefcase: Briefcase,
  Smartphone: Smartphone,

  // Formas
  Circle: Circle,
  Square: Square,
  Triangle: Triangle,

  // Educação
  GraduationCap: GraduationCap,

  // Outros
  Zap: Zap,
  Lightning: Zap,
  Activity: Activity,
  Target: Target,
  Layers: Layers,
  Image: Image,
  Package: Package,
  Layout: Layout,
  CheckCircle: CheckCircle,
  Rocket: Rocket,
};

// Função para aplicar máscara de telefone
const formatPhone = (value) => {
  const numbers = value.replace(/\D/g, "");
  if (numbers.length <= 2) {
    return numbers;
  } else if (numbers.length <= 7) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  } else if (numbers.length <= 11) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 3)}.${numbers.slice(
      3,
      7
    )}${numbers.slice(7) ? "-" + numbers.slice(7) : ""}`;
  }
  return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 3)}.${numbers.slice(
    3,
    7
  )}-${numbers.slice(7, 11)}`;
};

// A logo cadastrada da Capelania (bucket do Supabase) saiu do ar. Usamos a arte
// oficial publicada pelo HC-UFU/EBSERH no gov.br: uma faixa horizontal com as
// marcas UFU, HC-UFU, HUBrasil e Capelania.
const LOGO_OVERRIDES = {
  capelania:
    'https://www.gov.br/hubrasil/pt-br/hospitais-universitarios/regiao-sudeste/hc-ufu/ensino-e-pesquisa/solicitacao-de-servicos/solicitacao-de-visita-religiosa-1/logo_capelania.png/@@images/image'
};

// Data minima aceita por um campo de data, como "YYYY-MM-DD" no fuso local.
// Comparar strings evita o deslocamento de um dia que acontece ao passar por
// Date (o input entrega meia-noite UTC, mas "hoje" e calculado no fuso local).
const minDateString = (field) => {
  const days = field.validation?.min ?? 0;
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + days);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const hasValue = (value) =>
  Array.isArray(value)
    ? value.length > 0
    : value !== undefined && value !== null && value !== "";

// No resumo mostramos o rotulo da opcao escolhida, nao o valor interno que o
// formulario guarda (ex.: "Uncao dos Enfermos" em vez de "uncao").
const formatSummaryValue = (field, value) => {
  if (field.type === "date" && typeof value === "string") {
    return formatDateBR(value);
  }
  const labelFor = (item) =>
    field.options?.find((option) => option.value === item)?.label ?? item;
  return Array.isArray(value) ? value.map(labelFor).join(", ") : labelFor(value);
};

const formatDateBR = (isoDate) => {
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
};

const FormScreen = ({
  step,
  formData,
  updateFormData,
  flow,
  protocol,
  onNext,
  onPrev,
  onSubmit,
  onReset,
  onDownloadPDF,
  onCopyProtocol,
  onNavigate,
  submitting,
  submitted,
  isFirstStep,
  isLastStep,
  currentStepIndex,
  totalSteps,
  logo,
  navigationHistory = [],
  onNavigateBack
}) => {
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [autoAdvance, setAutoAdvance] = useState(false);
  const [isStepValid, setIsStepValid] = useState(false);
  const [showContactPage, setShowContactPage] = useState(null);
  const [logoFailed, setLogoFailed] = useState(false);

  // Buscar step atual
  const currentStep =
    flow.formConfig.steps?.find((s) => s.id === step) ||
    flow.formConfig.steps?.[currentStepIndex];

  const logoSrc = LOGO_OVERRIDES[flow?.url] || logo;
  const hasLogo = flow?.url === 'ugits' || (!!logoSrc && !logoFailed);

  // Efeito para validar o step quando formData muda ou no mount
  useEffect(() => {
    const stepErrors = collectStepErrors(formData);
    setErrors(stepErrors);
    setIsStepValid(Object.keys(stepErrors).length === 0);
  }, [formData, currentStep]);

  // Trocar de etapa cancela qualquer auto-avanco que ainda estivesse pendente,
  // senao a etapa seguinte herdaria o gatilho e pularia sozinha.
  useEffect(() => {
    setAutoAdvance(false);
  }, [currentStep?.id]);

  // Auto-avanco: o pequeno atraso deixa a escolha aparecer selecionada antes de
  // a tela trocar, para o usuario ver o que foi marcado.
  useEffect(() => {
    if (!autoAdvance) return undefined;
    const timer = setTimeout(() => {
      setAutoAdvance(false);
      handleNext();
    }, 280);
    return () => clearTimeout(timer);
  }, [autoAdvance]);

  // Garantir scroll ao topo quando step muda
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Early return após todos os hooks
  if (!currentStep && !submitted) {
    return (
      <div className="form-screen">
        <div className="screen-icon">
          <AlertCircle size={64} color="#e74c3c" />
        </div>
        <h2>Erro ao carregar etapa</h2>
        <p>A etapa solicitada não foi encontrada.</p>
      </div>
    );
  }

   // Função para gerar página de contato com WhatsApp
  const renderContactPage = (item) => {
    if (!item || !item.contactInfo) return null;
    
    // Converter URL do WhatsApp para o dispositivo correto
    const whatsappUrl = item.contactInfo.whatsapp ? 
      convertWhatsAppUrl(item.contactInfo.whatsapp) : null;
    
    const hasDirectWhatsApp = whatsappUrl && whatsappUrl.startsWith('https://');
    
    // Extrair mensagem pré-formatada do URL do WhatsApp
    let preformattedMessage = '';
    if (hasDirectWhatsApp) {
      try {
        const url = new URL(whatsappUrl);
        const text = url.searchParams.get('text');
        if (text) {
          preformattedMessage = decodeURIComponent(text);
        }
      } catch (e) {
        console.error('Erro ao extrair mensagem:', e);
      }
    }
    
    return (
      <div className="contact-page">
        <div className="contact-page-icon">
          <MessageCircle size={40} color="white" />
        </div>
        
        <h2>{item.label}</h2>
        
        <p className="contact-page-intro">
          Quer saber mais sobre <strong>{item.label.toLowerCase()}</strong>? 
          Nossa equipe está pronta para esclarecer suas dúvidas!
        </p>
        
        {item.description && (
          <p className="contact-page-description">
            {item.description}
          </p>
        )}
        
        {/* Informações de contato */}
        {(item.contactInfo.hours || item.contactInfo.address || item.contactInfo.email || item.contactInfo.phone) && (
          <div className="contact-info-box">
            {item.contactInfo.hours && (
              <div className="contact-info-item">
                <Clock size={18} />
                <span>{item.contactInfo.hours}</span>
              </div>
            )}
            
            {item.contactInfo.address && (
              <div className="contact-info-item">
                <MapPin size={18} />
                <span>{item.contactInfo.address}</span>
              </div>
            )}
            
            {item.contactInfo.email && (
              <div className="contact-info-item">
                <Mail size={18} />
                <span>{item.contactInfo.email}</span>
              </div>
            )}
            
            {item.contactInfo.phone && (
              <div className="contact-info-item">
                <Phone size={18} />
                <span>{item.contactInfo.phone}</span>
              </div>
            )}
          </div>
        )}
        
        {hasDirectWhatsApp && preformattedMessage && (
          <div className="message-preview-box">
            <div className="message-preview-header">
              <Info size={16} />
              <span>Mensagem que será enviada:</span>
            </div>
            <div className="message-preview-content">
              <pre>{preformattedMessage}</pre>
            </div>
            <p className="message-preview-note">
              Você pode editar a mensagem antes de enviar!
            </p>
          </div>
        )}
        
        {hasDirectWhatsApp && (
          <button 
            onClick={() => window.open(whatsappUrl, '_blank')} // Usa a URL já convertida
            className="btn btn-primary btn-whatsapp"
          >
            <MessageCircle size={20} />
            Iniciar Conversa no WhatsApp
          </button>
        )}
        
        {!hasDirectWhatsApp && item.contactInfo.whatsapp && (
          <p className="contact-page-description">
            Entre em contato através dos canais disponíveis acima.
          </p>
        )}
        
        <button 
          onClick={() => setShowContactPage(null)} 
          className="btn btn-secondary"
          style={{ marginTop: '20px' }}
        >
          <ArrowLeft size={20} />
          Voltar
        </button>
      </div>
    );
  };

  // Renderizar ícone
  // Cabecalho de marca. A logo da Capelania e uma faixa larga com varias marcas,
  // por isso vai numa moldura propria (ver .form-logo-icon no tema). Se a imagem
  // nao carregar, cai no icone da etapa em vez de deixar um quadro quebrado.
  const renderLogo = () => {
    if (flow?.url === 'ugits') {
      return (
        <div className="form-logo-icon">
          <UgitsLogo />
        </div>
      );
    }
    if (!logoSrc || logoFailed) return null;
    return (
      <div className="form-logo-icon">
        <img
          src={logoSrc}
          alt={flow?.formConfig?.title || "Logo"}
          onError={() => setLogoFailed(true)}
        />
      </div>
    );
  };

  const renderIcon = (iconName, size = 20) => {
    if (!iconName) return null;
    const IconComponent = ICON_MAP[iconName];
    return IconComponent ? <IconComponent size={size} /> : null;
  };

  // Verificar condicoes contra um conjunto de dados qualquer, para que a
  // validacao possa ser feita sobre um valor que ainda nao entrou no estado.
  const checkConditionWith = (field, data) => {
    if (!field.conditional?.show) return true;
    const { when, equals } = field.conditional;
    const currentValue = data[when];
    if (Array.isArray(currentValue)) {
      return currentValue.includes(equals);
    }
    return currentValue == equals;
  };

  const checkCondition = (field) => checkConditionWith(field, formData);

  // Validar campo individual
  const validateField = (field, value) => {
    const fieldErrors = [];

    // Campos de navegação não precisam validação
    if (field.type === 'navigation') {
      return fieldErrors;
    }

    // Required
    if (field.required) {
      if (
        !value ||
        (Array.isArray(value) && value.length === 0) ||
        value === "" ||
        value === null ||
        value === undefined
      ) {
        fieldErrors.push(
          field.validation?.customMessage || `${field.label} é obrigatório`
        );
      }
    }

    // Validacoes por tipo. Telefone e data valem mesmo sem bloco `validation`
    // no cadastro do campo: o formato faz parte do proprio tipo.
    if (value) {
      switch (field.type) {
        case "text":
        case "textarea":
          if (
            field.validation?.minLength &&
            value.length < field.validation.minLength
          ) {
            fieldErrors.push(
              `Mínimo de ${field.validation.minLength} caracteres`
            );
          }
          if (
            field.validation?.maxLength &&
            value.length > field.validation.maxLength
          ) {
            fieldErrors.push(
              `Máximo de ${field.validation.maxLength} caracteres`
            );
          }
          if (field.validation?.pattern) {
            const regex = new RegExp(field.validation.pattern);
            if (!regex.test(value)) {
              fieldErrors.push(
                field.validation.customMessage || "Formato inválido"
              );
            }
          }
          break;

        case "number": {
          const numValue = parseFloat(value);
          if (
            field.validation?.min !== undefined &&
            numValue < field.validation.min
          ) {
            fieldErrors.push(`Valor mínimo: ${field.validation.min}`);
          }
          if (
            field.validation?.max !== undefined &&
            numValue > field.validation.max
          ) {
            fieldErrors.push(`Valor máximo: ${field.validation.max}`);
          }
          break;
        }

        case "tel":
        case "phone": {
          const phoneNumbers = String(value).replace(/\D/g, "");
          if (phoneNumbers.length < 10 || phoneNumbers.length > 11) {
            fieldErrors.push("Telefone inválido. Use: (00) 0.0000-0000");
          }
          break;
        }

        case "date": {
          const earliest = minDateString(field);
          if (value < earliest) {
            fieldErrors.push(
              `Escolha uma data a partir de ${formatDateBR(earliest)}`
            );
          }
          break;
        }

        default:
          break;
      }
    }

    // Validação de email sempre ativa
    if (field.type === "email" && value) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(value)) {
        fieldErrors.push("Email inválido. Exemplo: seuemail@exemplo.com");
      }
    }

    return fieldErrors;
  };

  // Erros do step para um conjunto de dados, sem efeito colateral. Assim da
  // para perguntar "o step ficaria valido com este valor?" antes de grava-lo.
  const collectStepErrors = (data) => {
    const newErrors = {};

    // Steps de navegação não precisam validação
    if (!currentStep || currentStep.type === 'navigation') return newErrors;

    currentStep.fields.forEach((field) => {
      if (!checkConditionWith(field, data)) return;
      if (["section", "info", "navigation"].includes(field.type)) return;

      const fieldErrors = validateField(field, data[field.id]);
      if (fieldErrors.length > 0) {
        newErrors[field.id] = fieldErrors.join(", ");
      }
    });

    return newErrors;
  };

  const isStepValidWith = (data) =>
    Object.keys(collectStepErrors(data)).length === 0;

  // Validar step completo
  const validateStep = () => {
    const newErrors = collectStepErrors(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Lidar com mudança de campo
  const handleFieldChange = (fieldId, value, fieldType) => {
    if (fieldType === "tel" || fieldType === "phone") {
      value = formatPhone(value);
    }

    const nextData = { ...formData, [fieldId]: value };

    updateFormData({ [fieldId]: value });
    setTouchedFields((prev) => ({ ...prev, [fieldId]: true }));

    const field = currentStep.fields.find((f) => f.id === fieldId);
    if (field) {
      const fieldErrors = validateField(field, value);
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (fieldErrors.length > 0) {
          newErrors[fieldId] = fieldErrors.join(", ");
        } else {
          delete newErrors[fieldId];
        }
        return newErrors;
      });
    }

    // Auto-avanco so em escolha de opcao unica. Checkbox e multi-selecao:
    // avancar no primeiro clique impediria marcar os demais servicos. E na
    // ultima etapa avancar significa enviar, o que nunca pode ser automatico.
    // A validacao roda sobre `nextData` porque a escolha pode revelar campos
    // condicionais obrigatorios que ainda estao vazios.
    if (
      ["button", "radio"].includes(fieldType) &&
      !isLastStep &&
      isStepValidWith(nextData)
    ) {
      setAutoAdvance(true);
    }
  };

  // Lidar com blur
  const handleFieldBlur = (fieldId) => {
    setTouchedFields({ ...touchedFields, [fieldId]: true });

    const field = currentStep.fields.find((f) => f.id === fieldId);
    if (field) {
      const value = formData[fieldId];
      const fieldErrors = validateField(field, value);
      if (fieldErrors.length > 0) {
        setErrors({ ...errors, [fieldId]: fieldErrors.join(", ") });
      } else {
        const newErrors = { ...errors };
        delete newErrors[fieldId];
        setErrors(newErrors);
      }
    }
  };

  // Lidar com próximo
  const handleNext = () => {
    if (validateStep()) {
      if (isLastStep) {
        onSubmit();
      } else {
        onNext();
      }
    }
  };

  // Renderizar item de navegação
  const renderNavigationItem = (item) => {
    const getVariantClass = () => {
      switch (item.variant) {
        case 'primary': return 'nav-item-primary';
        case 'success': return 'nav-item-success';
        case 'warning': return 'nav-item-warning';
        case 'danger': return 'nav-item-danger';
        case 'info': return 'nav-item-info';
        default: return '';
      }
    };

    const getStatusBadge = () => {
      if (!item.status || item.status === 'active') return null;
      
      const statusLabels = {
        'development': '🚧 Em desenvolvimento',
        'suspended': item.statusMessage || '⚠️ Suspenso',
        'pilot': '🧪 Piloto',
        'coming-soon': '🔜 Em breve'
      };

      return (
        <span className={`nav-status-badge status-${item.status}`}>
          {statusLabels[item.status] || item.statusMessage}
        </span>
      );
    };

    const handleItemClick = () => {
      switch (item.type) {
        case 'navigate':
          if (onNavigate) {
            onNavigate(item.targetStep);
          }
          break;
        case 'link':
          // Se for link do WhatsApp, converter antes de abrir
          const finalUrl = item.url?.includes('whatsapp.com') ? 
            convertWhatsAppUrl(item.url) : item.url;
          window.open(finalUrl, '_blank');
          break;
        case 'contact':
          setShowContactPage(item);
          break;
        case 'crosslink':
          if (onNavigate) {
            onNavigate(item.crosslinkTarget);
          }
          break;
        default:
          break;
      }
    };

    return (
      <div
        key={item.id}
        className={`navigation-item ${getVariantClass()}`}
        onClick={handleItemClick}
      >
        <div className="nav-item-header">
          {item.emoji && <span className="nav-emoji">{item.emoji}</span>}
          {item.icon && renderIcon(item.icon, 24)}
          <div className="nav-item-title">
            <h4>{item.label}</h4>
            {item.badge && <span className="nav-badge">{item.badge}</span>}
          </div>
          <div className="nav-item-action">
            {item.type === 'navigate' && <ChevronRight size={20} />}
            {item.type === 'link' && <ExternalLink size={20} />}
            {item.type === 'contact' && <MessageCircle size={20} />}
            {item.type === 'crosslink' && <Link2 size={20} />}
          </div>
        </div>
        {item.description && (
          <p className="nav-item-description">{item.description}</p>
        )}
        {getStatusBadge()}
      </div>
    );
  };

  // Renderizar campo
  const renderField = (field) => {
    if (!checkCondition(field)) return null;

    const fieldError = errors[field.id];
    const hasError = !!fieldError && touchedFields[field.id];

    // Novo tipo: navigation
    if (field.type === 'navigation') {
      const layoutClass = field.navigationLayout || 'cards';
      return (
        <div key={field.id} className={`navigation-field layout-${layoutClass}`}>
          {field.label && <h3>{field.label}</h3>}
          {field.description && <p>{field.description}</p>}
          <div className={`navigation-items ${layoutClass}`}>
            {field.navigationItems?.map(item => renderNavigationItem(item))}
          </div>
        </div>
      );
    }

    switch (field.type) {
      case "section":
        return (
          <div key={field.id} className="form-section-divider">
            <h3 translate="no">{field.label}</h3>
            {field.description && <p translate="no">{field.description}</p>}
          </div>
        );

      case "info":
        if (field.style?.variant === "large") {
          return (
            <div key={field.id} className="alert-info large">
              <table>
                <tbody>
                  <tr>
                    <td rowSpan="2" className="icon-cell">
                      {field.icon && renderIcon(field.icon)}
                    </td>
                    <td className="content-cell">
                      <strong translate="no">{field.label}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td className="content-cell">
                      <p translate="no">{field.description}</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        }

        return (
          <div
            key={field.id}
            className={`alert-info ${field.style?.variant || ""}`}
          >
            {field.icon && renderIcon(field.icon)}
            <div>
              {field.label && <strong translate="no">{field.label}</strong>}
              <p translate="no">{field.description}</p>
            </div>
          </div>
        );

      case "text":
        return (
          <div
            key={field.id}
            className={`form-group ${field.style?.width || "full"}`}
          >
            <label translate="no">
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            <input
              type={field.type}
              value={formData[field.id] || ""}
              onChange={(e) =>
                handleFieldChange(field.id, e.target.value, field.type)
              }
              placeholder={field.placeholder}
              className={hasError ? "error" : ""}
              onBlur={() => handleFieldBlur(field.id)}
            />
            {field.description && !hasError && (
              <span className="field-description" translate="no">{field.description}</span>
            )}
            {hasError && <span className="error-message">{fieldError}</span>}
          </div>
        );

      case "email":
        return (
          <div
            key={field.id}
            className={`form-group ${field.style?.width || "full"}`}
          >
            <label translate="no">
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            <input
              type="email"
              value={formData[field.id] || ""}
              onChange={(e) =>
                handleFieldChange(field.id, e.target.value, field.type)
              }
              placeholder={field.placeholder}
              className={hasError ? "error" : ""}
              onBlur={() => handleFieldBlur(field.id)}
            />
            {field.description && !hasError && (
              <span className="field-description" translate="no">{field.description}</span>
            )}
            {hasError && <span className="error-message">{fieldError}</span>}
          </div>
        );

      case "number":
        return (
          <div
            key={field.id}
            className={`form-group ${field.style?.width || "full"}`}
          >
            <label translate="no">
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            <input
              type="number"
              inputMode="numeric"
              value={formData[field.id] || ""}
              onChange={(e) =>
                handleFieldChange(field.id, e.target.value, field.type)
              }
              placeholder={field.placeholder}
              className={hasError ? "error" : ""}
              onBlur={() => handleFieldBlur(field.id)}
            />
            {field.description && !hasError && (
              <span className="field-description" translate="no">{field.description}</span>
            )}
            {hasError && <span className="error-message">{fieldError}</span>}
          </div>
        );

      case "tel":
      case "phone":
        return (
          <div
            key={field.id}
            className={`form-group ${field.style?.width || "full"}`}
          >
            <label translate="no">
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            <input
              type="tel"
              inputMode="tel"
              value={formData[field.id] || ""}
              onChange={(e) =>
                handleFieldChange(field.id, e.target.value, field.type)
              }
              placeholder={field.placeholder || "(00) 0.0000-0000"}
              className={hasError ? "error" : ""}
              onBlur={() => handleFieldBlur(field.id)}
              maxLength="16"
            />
            {field.description && !hasError && (
              <span className="field-description" translate="no">{field.description}</span>
            )}
            {hasError && <span className="error-message">{fieldError}</span>}
          </div>
        );

      case "textarea":
        return (
          <div
            key={field.id}
            className={`form-group ${field.style?.width || "full"}`}
          >
            <label translate="no">
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            <textarea
              value={formData[field.id] || ""}
              onChange={(e) =>
                handleFieldChange(field.id, e.target.value, field.type)
              }
              placeholder={field.placeholder}
              rows={3}
              className={hasError ? "error" : ""}
              onBlur={() => handleFieldBlur(field.id)}
            />
            {field.description && !hasError && (
              <span className="field-description" translate="no">{field.description}</span>
            )}
            {hasError && <span className="error-message">{fieldError}</span>}
          </div>
        );

      case "select":
        return (
          <div
            key={field.id}
            className={`form-group ${field.style?.width || "full"}`}
          >
            <label translate="no">
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            <select
              value={formData[field.id] || ""}
              onChange={(e) =>
                handleFieldChange(field.id, e.target.value, field.type)
              }
              className={hasError ? "error" : ""}
              onBlur={() => handleFieldBlur(field.id)}
            >
              <option value="">{field.placeholder || "Selecione..."}</option>
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {field.description && !hasError && (
              <span className="field-description" translate="no">{field.description}</span>
            )}
            {hasError && <span className="error-message">{fieldError}</span>}
          </div>
        );

      case "radio":
        return (
          <div key={field.id} className="form-group">
            <label className="group-label" translate="no">
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            {field.description && (
              <p className="field-description" translate="no">{field.description}</p>
            )}
            <div
              className={`radio-options ${
                field.style?.variant === "card" ? "card-style" : ""
              }`}
            >
              {field.options?.map((option) => (
                <label key={option.value} className="radio-card">
                  <input
                    type="radio"
                    name={field.id}
                    value={option.value}
                    checked={formData[field.id] === option.value}
                    onChange={(e) =>
                      handleFieldChange(field.id, e.target.value, field.type)
                    }
                  />
                  <div className="radio-content">
                    {option.icon && renderIcon(option.icon, 32)}
                    <h3 translate="no">{option.label}</h3>
                    {option.description && <p translate="no">{option.description}</p>}
                  </div>
                </label>
              ))}
            </div>
            {hasError && <span className="error-message">{fieldError}</span>}
          </div>
        );

      case "checkbox":
        return (
          <div key={field.id} className="form-group">
            {field.label && (
              <label className="group-label" translate="no">
                {field.label}
                {field.required && <span className="required">*</span>}
              </label>
            )}
            {field.description && (
              <p className="field-description" translate="no">{field.description}</p>
            )}
            <div className="checkbox-list">
              {field.options?.map((option) => (
                <label key={option.value} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={(formData[field.id] || []).includes(option.value)}
                    onChange={(e) => {
                      const current = formData[field.id] || [];
                      if (e.target.checked) {
                        handleFieldChange(
                          field.id,
                          [...current, option.value],
                          field.type
                        );
                      } else {
                        handleFieldChange(
                          field.id,
                          current.filter((v) => v !== option.value),
                          field.type
                        );
                      }
                      setTouchedFields({ ...touchedFields, [field.id]: true });
                    }}
                  />
                  <span className="checkbox-label" translate="no">
                    {option.icon && renderIcon(option.icon, 16)}
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
            {hasError && <span className="error-message">{fieldError}</span>}
          </div>
        );

      case "button":
        return (
          <div key={field.id} className="form-group">
            {field.label && (
              <label className="group-label" translate="no">
                {field.label}
                {field.required && <span className="required">*</span>}
              </label>
            )}
            {field.description && (
              <p className="field-description" translate="no">{field.description}</p>
            )}
            <div
              className={`options-grid ${
                field.style?.variant === "card" ? "card-style" : ""
              }`}
            >
              {field.options?.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`option-card ${
                    formData[field.id] === option.value ? "selected" : ""
                  }`}
                  onClick={() => {
                    handleFieldChange(field.id, option.value, field.type);
                    setTouchedFields({ ...touchedFields, [field.id]: true });
                  }}
                >
                  {option.icon &&
                    renderIcon(
                      option.icon,
                      field.style?.variant === "card" ? 40 : 24
                    )}
                  <h3 translate="no">{option.label}</h3>
                  {option.description && <p translate="no">{option.description}</p>}
                </button>
              ))}
            </div>
            {hasError && <span className="error-message">{fieldError}</span>}
          </div>
        );

      case "date":
        return (
          <div
            key={field.id}
            className={`form-group ${field.style?.width || "full"}`}
          >
            <label translate="no">
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            <input
              type="date"
              value={formData[field.id] || ""}
              onChange={(e) =>
                handleFieldChange(field.id, e.target.value, field.type)
              }
              min={minDateString(field)}
              className={hasError ? "error" : ""}
              onBlur={() => handleFieldBlur(field.id)}
            />
            {field.description && !hasError && (
              <span className="field-description" translate="no">{field.description}</span>
            )}
            {hasError && <span className="error-message">{fieldError}</span>}
          </div>
        );

      default:
        return null;
    }
  };

  // Renderizar breadcrumbs
  const renderBreadcrumbs = () => {
    if (!currentStep?.showBreadcrumbs || !navigationHistory?.length) return null;

    return (
      <div className="navigation-breadcrumbs">
        <button onClick={() => onNavigate && onNavigate('home')} className="breadcrumb-item">
          <Home size={14} />
          Início
        </button>
        {navigationHistory.map((historyStep, index) => {
          const step = flow.formConfig.steps.find(s => s.id === historyStep);
          if (!step) return null;
          
          return (
            <React.Fragment key={historyStep}>
              <ChevronRight size={14} className="breadcrumb-separator" />
              <button 
                onClick={() => onNavigateBack && onNavigateBack(index)}
                className="breadcrumb-item"
              >
                {step.title}
              </button>
            </React.Fragment>
          );
        })}
        {currentStep && (
          <>
            <ChevronRight size={14} className="breadcrumb-separator" />
            <span className="breadcrumb-current">{currentStep.title}</span>
          </>
        )}
      </div>
    );
  };

  // Se tiver uma página de contato para mostrar
  if (showContactPage) {
    return (
      <>

{/* AISearchBox deve sempre estar presente para SGPITS/UGITS */}
<AISearchBox 
  flow={flow}
  currentStep={currentStep?.id}
  onNavigate={onNavigate}
  embedded={false}
  debug={false}
/>
        <div className="form-screen is-step">
          {renderBreadcrumbs()}
          {renderLogo()}
          {renderContactPage(showContactPage)}
        </div>
      </>
    );
  }

  // Renderizar tela de sucesso
  if (submitted && protocol) {
    return (
      <div className="form-screen sucesso is-step">
        {renderLogo()}
        <div className="success-icon">
          <CheckCircle size={80} color="#27ae60" />
        </div>

        <h1>{flow.formConfig.successMessage || "Formulário Enviado!"}</h1>

        {flow.formConfig.settings?.generateProtocol !== false && (
          <>
            <p className="success-message">
              Sua solicitação foi registrada com sucesso
            </p>
            <div className="protocol-card">
              <h3>Número do Protocolo</h3>
              <div className="protocol-number">{protocol}</div>
              <button onClick={onCopyProtocol} className="btn-icon">
                <Copy size={20} />
                Copiar
              </button>
            </div>
          </>
        )}

        <div className="success-actions">
          <button onClick={onDownloadPDF} className="btn btn-secondary">
            <Download size={20} />
            Baixar Comprovante
          </button>

          <button onClick={onReset} className="btn btn-outline">
            <Home size={20} />
            Nova Solicitação
          </button>
        </div>

        {(flow.formConfig.urgentPhone || flow.formConfig.email) && (
          <div className="contact-info">
            <p>Em caso de dúvidas:</p>
            <div className="contact-items">
              {flow.formConfig.urgentPhone && (
                <span>
                  <Phone size={16} /> {flow.formConfig.urgentPhone}
                </span>
              )}
              {flow.formConfig.email && (
                <span>
                  <Mail size={16} /> {flow.formConfig.email}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Renderizar tela de confirmação
  if (isLastStep && flow.formConfig.settings?.requireConfirmation !== false && currentStep?.type !== 'navigation') {
    return (
      <>
        {/* AISearchBox deve sempre estar presente */}
{/* AISearchBox deve sempre estar presente para SGPITS/UGITS */}
<AISearchBox 
  flow={flow}
  currentStep={currentStep?.id}
  onNavigate={onNavigate}
  embedded={false}
  debug={false}
/>
        
        <div className="form-screen confirmacao is-step">
          {renderLogo()}

          {!hasLogo && currentStep?.icon && (
            <div className="screen-icon">
              {renderIcon(currentStep.icon, 64)}
            </div>
          )}

          <h2 translate="no">{currentStep?.title || "Confirme os dados"}</h2>
          {currentStep?.description && <p translate="no">{currentStep.description}</p>}

          {/* Resumo: so as etapas ja respondidas. Etapas sem nenhum campo
              preenchido eram renderizadas so com o titulo, deixando blocos
              vazios no meio da revisao. */}
          <div className="summary-card">
            {flow.formConfig.steps
              .filter((step) => step.id !== currentStep?.id)
              .map((step) => {
                const answered = step.fields.filter(
                  (field) =>
                    !["section", "info", "navigation"].includes(field.type) &&
                    checkCondition(field) &&
                    hasValue(formData[field.id])
                );

                if (answered.length === 0) return null;

                return (
                  <div key={step.id} className="summary-section">
                    <h3 translate="no">{step.title}</h3>
                    {answered.map((field) => (
                      <div key={field.id} className="summary-item">
                        <span className="label" translate="no">
                          {field.label}:
                        </span>
                        <span className="value" translate="no">
                          {formatSummaryValue(field, formData[field.id])}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}
          </div>

          <div className="fields-wrapper">
            {currentStep?.fields.map((field) => renderField(field))}
          </div>

          <div className="form-navigation">
            {currentStep?.navigation?.showPrevious !== false && !isFirstStep && (
              <button onClick={onPrev} className="btn btn-secondary">
                <ChevronLeft size={20} />
                {currentStep?.navigation?.previousButtonText || "Voltar"}
              </button>
            )}

            <button
              onClick={handleNext}
              className="btn btn-primary"
              disabled={submitting || !isStepValid}
            >
              {submitting
                ? "Enviando..."
                : currentStep?.navigation?.nextButtonText || "Enviar"}
              {!submitting && <Send size={20} />}
            </button>
          </div>
        </div>
      </>
    );
  }

  // Renderização principal com AISearchBox SEMPRE visível
  return (
    <>
{/* AISearchBox deve sempre estar presente para SGPITS/UGITS */}
<AISearchBox 
  flow={flow}
  currentStep={currentStep?.id}
  onNavigate={onNavigate}
  embedded={false}
  debug={false}
/>
      
      <div
        className={`form-screen ${
          currentStep?.type === 'navigation' ? 'navigation-screen' : ''
        } ${isFirstStep ? 'is-landing' : 'is-step'}`}
      >
        {renderBreadcrumbs()}
        
        {renderLogo()}

        {!hasLogo && currentStep?.icon && (
          <div className="screen-icon">{renderIcon(currentStep.icon, 64)}</div>
        )}

        <h2 translate="no">{currentStep?.title}</h2>
        {currentStep?.description && <p translate="no">{currentStep.description}</p>}

        {currentStep?.type === 'navigation' ? (
          <>
            <div className="fields-wrapper">
              {currentStep?.fields
                .sort((a, b) => a.order - b.order)
                .map((field) => renderField(field))}
            </div>
            
            {/* Botão home ou voltar para navegação */}
            <div className="form-navigation">
              {currentStep?.navigation?.showHome && (
                <button onClick={() => onNavigate && onNavigate('home')} className="btn btn-outline">
                  <Home size={20} />
                  Voltar ao Início
                </button>
              )}
              {currentStep?.navigation?.showPrevious && navigationHistory?.length > 0 && (
                <button onClick={() => onNavigateBack && onNavigateBack()} className="btn btn-secondary">
                  <ChevronLeft size={20} />
                  {currentStep?.navigation?.previousButtonText || "Voltar"}
                </button>
              )}
            </div>
          </>
        ) : (
          // Navegação tradicional para formulários
          <>
            {currentStepIndex === 0 ? (
              <>
                <div className="form-navigation">
                  {currentStep?.navigation?.showPrevious !== false &&
                    !isFirstStep && (
                      <button onClick={onPrev} className="btn btn-secondary">
                        <ChevronLeft size={20} />
                        {currentStep?.navigation?.previousButtonText || "Voltar"}
                      </button>
                    )}

                  <button
                    onClick={handleNext}
                    className="btn btn-primary"
                    disabled={submitting || !isStepValid}
                  >
                    {currentStep?.navigation?.nextButtonText || "Continuar"}
                    <ChevronRight size={20} />
                  </button>
                </div>

                <div className="fields-wrapper">
                  {currentStep?.fields
                    .sort((a, b) => a.order - b.order)
                    .map((field) => renderField(field))}
                </div>
              </>
            ) : (
              <>
                <div className="fields-wrapper">
                  {currentStep?.fields
                    .sort((a, b) => a.order - b.order)
                    .map((field) => renderField(field))}
                </div>

                <div className="form-navigation">
                  {currentStep?.navigation?.showPrevious !== false &&
                    !isFirstStep && (
                      <button onClick={onPrev} className="btn btn-secondary">
                        <ChevronLeft size={20} />
                        {currentStep?.navigation?.previousButtonText || "Voltar"}
                      </button>
                    )}

                  <button
                    onClick={handleNext}
                    className="btn btn-primary"
                    disabled={submitting || !isStepValid}
                  >
                    {currentStep?.navigation?.nextButtonText || "Continuar"}
                    <ChevronRight size={20} />
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default FormScreen;